import { useMemo, useState } from 'react'
import { GripVertical, HelpCircle, Plus, Trash2 } from 'lucide-react'
import { Tooltip } from '../../../components/ui/Tooltip'
import { useScopeStore } from '../store'
import { useHasResellerParent, ResellerInheritedBanner } from './ResellerLock'
import { AML_RELATIONSHIPS, AML_VENDORS, KYC_RULESETS, V1_RECIPES, V1_CHECK_TYPE_OPTIONS, WORKFLOW_CASCADE_SOURCES } from '../types'
import type { BuilderWorkflow, RiskBranch, RiskBranchOutcome, ScopeData, V1CustomCheck, V1RecipeDefinition, WorkflowStep, WorkflowStepType } from '../types'
import { getFieldById, getGroupById } from '../schema'

const WORKFLOW_STEP_OPTIONS: Array<{ id: WorkflowStepType; label: string }> = [
  { id: 'blocklist', label: 'Blocklist' },
  { id: 'gov_id', label: 'Gov ID' },
  { id: 'kyc', label: 'KYC' },
  { id: 'death_check', label: 'AU Death Check' },
  { id: 'idv_biometrics', label: 'IDV/Biometrics' },
  { id: 'aml', label: 'AML' },
  { id: 'fraud', label: 'Fraud' },
  { id: 'risk', label: 'Risk' },
  { id: 'duplicate', label: 'Duplicate Check' },
]

const stepLabel = (type: WorkflowStepType): string => {
  const o = WORKFLOW_STEP_OPTIONS.find((x) => x.id === type)
  return o?.label || type
}

const isStepType = (value: string): value is WorkflowStepType => {
  return WORKFLOW_STEP_OPTIONS.some((o) => o.id === value)
}

const createStep = (type: WorkflowStepType): WorkflowStep => {
  const id = `step_${Date.now()}_${Math.random().toString(16).slice(2)}`
  if (type === 'blocklist') return { id, type }
  if (type === 'gov_id') return { id, type }
  if (type === 'death_check') return { id, type }
  if (type === 'aml') return { id, type }
  if (type === 'fraud') return { id, type }
  if (type === 'duplicate') return { id, type, notes: '' }
  if (type === 'kyc') return { id, type, rulesetId: '', cascadeSources: [] }
  if (type === 'idv_biometrics') return { id, type, flowId: null, requirement: 'mandatory' }
  return { id, type, branches: [] }
}

const createBranch = (): RiskBranch => ({
  id: `branch_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  condition: '',
  outcome: '',
  steps: [],
})

function formatIdvFlowSummary(
  step: Extract<WorkflowStep, { type: 'idv_biometrics' }>,
  scope: ScopeData,
  workflowAssociatedFlowId: string | null
) {
  if (scope.biometricsConfig.enabled !== 'yes') {
    const stateLabel = scope.biometricsConfig.enabled === 'no' ? 'No' : "Don't know"
    return {
      selectedFlowId: null,
      summary: `ID Verification is set to ${stateLabel}`,
    }
  }

  const flows = Array.isArray(scope.biometricsConfig.flows) ? scope.biometricsConfig.flows : []
  const defaultFlow = flows.find((f) => Boolean(f?.isDefault))
  const selectedFlowId = step.flowId || workflowAssociatedFlowId || defaultFlow?.id || null
  const selectedFlow = flows.find((f) => f?.id === selectedFlowId) || null

  if (!selectedFlow) {
    return {
      selectedFlowId,
      summary: 'No flow selected',
    }
  }

  const stepsText = [
    selectedFlow.consentScreenEnabled === 'yes' ? 'Consent' : null,
    selectedFlow.idVerificationEnabled === 'yes' ? 'IDV' : null,
    selectedFlow.biometricsEnabled === 'yes' ? 'Biometrics' : null,
    selectedFlow.reviewScreenEnabled === 'yes' ? 'Review' : null,
    selectedFlow.redirectPageEnabled === 'yes' ? 'Redirect' : null,
    selectedFlow.customScreenEnabled === 'yes' ? `Custom${selectedFlow.customScreenDetails ? ` (${selectedFlow.customScreenDetails})` : ''}` : null,
  ]
    .filter(Boolean)
    .join(' > ')

  const docs =
    Array.isArray(selectedFlow.documentTypes) && selectedFlow.documentTypes.length
      ? selectedFlow.documentTypes.filter((d) => d !== 'unknown').join(', ') || '—'
      : '—'

  return {
    selectedFlowId,
    flowName: selectedFlow.name || selectedFlow.id,
    flowSteps: stepsText || '—',
    flowVendor: selectedFlow.vendor,
    flowDocs: docs,
  }
}

/**
 * Returns a reason string if this V1 recipe conflicts with the current KYC/AML config,
 * or null if the recipe is compatible.
 */
function getV1RecipeDisabledReason(
  recipe: V1RecipeDefinition,
  amlLevel: string,
  v1RulesetKeys: string[],
): string | null {
  // ── AML mismatch ────────────────────────────────────────────────────────
  // Only check when AML level is explicitly set to standard or enhanced
  if (amlLevel === 'standard' && recipe.amlLevel === 'enhanced') {
    return 'AML is set to Standard — this recipe requires Enhanced AML'
  }
  if (amlLevel === 'enhanced' && recipe.amlLevel === 'standard') {
    return 'AML is set to Enhanced — this recipe uses Standard AML'
  }

  // ── KYC verification level mismatch ─────────────────────────────────────
  // Only check when V1 KYC rulesets have been configured
  if (v1RulesetKeys.length === 0) return null

  // Determine which verification level groups are configured
  const has1Plus1 = v1RulesetKeys.includes('v1_one_plus_one')
  const has2Plus2 = v1RulesetKeys.includes('v1_two_plus_two') || v1RulesetKeys.includes('v1_two_plus_two_name_address')
  const hasManual = v1RulesetKeys.includes('v1_manual_kyc')

  // Determine what the recipe needs
  const needs1Plus1 = recipe.checkTypes.includes('1+1')
  const needs2Plus2 = recipe.checkTypes.includes('2+2') || recipe.checkTypes.includes('2+2 (Name + Address)')

  // If recipe has no verification level (AML-only, Gov ID-only, Business) → no KYC check needed
  if (!needs1Plus1 && !needs2Plus2) return null

  // Manual KYC is universal — don't filter
  if (hasManual) return null

  if (needs1Plus1 && !has1Plus1) {
    const configured = has2Plus2 ? '2+2' : 'unknown'
    return `KYC is set to ${configured} — this recipe requires 1+1`
  }
  if (needs2Plus2 && !has2Plus2) {
    const configured = has1Plus1 ? '1+1' : 'unknown'
    return `KYC is set to ${configured} — this recipe requires 2+2`
  }

  return null
}

export function WorkflowsTab() {
  const {
    scope,
    addWorkflow,
    addComplexWorkflow,
    addPresetWorkflow,
    addV1RecipeWorkflow,
    addV1CustomWorkflow,
    updateWorkflow,
    removeWorkflow,
    addWorkflowStep,
    updateWorkflowStep,
    removeWorkflowStep,
    reorderWorkflowStep,
    addKycRuleset,
    updateKycConfig,
    updateWorkflowsMeta,
  } = useScopeStore()

  const resellerImported = useHasResellerParent()
  const disabled = scope.finalized || resellerImported
  const { workflowsConfig } = scope
  const isV1 = scope.generalInfo.platformVersion === 'v1'

  const idvEnabled = scope.biometricsConfig.enabled === 'yes'
  const idvDisabledLabel = scope.biometricsConfig.enabled === 'no' ? 'No' : "Don't know"

  const idvFlows = useMemo(() => {
    const flows = scope.biometricsConfig.flows
    return Array.isArray(flows) ? flows : []
  }, [scope.biometricsConfig])

  const amlRelationshipLabel =
    AML_RELATIONSHIPS.find((r) => r.id === scope.amlConfig.relationship)?.label || scope.amlConfig.relationship || '—'
  const amlVendorLabel = AML_VENDORS.find((v) => v.id === scope.amlConfig.vendor)?.label || scope.amlConfig.vendor || '—'
  const amlCoverage =
    !scope.amlConfig.level
      ? '—'
      : scope.amlConfig.level === 'na'
        ? 'N/A'
        : scope.amlConfig.level === 'enhanced'
          ? 'PEPs, Sanctions and Media'
          : 'PEPs and Sanctions'

  const amlConfigured = Boolean(scope.amlConfig.level)
  const amlStepAllowed = Boolean(scope.amlConfig.level) && scope.amlConfig.level !== 'na'
  const [amlAddError, setAmlAddError] = useState<string | null>(null)

  // V1 KYC ruleset keys for recipe compatibility checking
  const v1RulesetKeys = useMemo(
    () => scope.kycConfig.rulesets.map((r) => r.rulesetKey).filter((k) => k.startsWith('v1_')),
    [scope.kycConfig.rulesets],
  )

  const [draggingStepId, setDraggingStepId] = useState<string | null>(null)
  const [draggingCascadeId, setDraggingCascadeId] = useState<string | null>(null)

  const creditHeaderAllowed = useMemo(() => {
    const mode = scope.kycConfig.enableConsentSources
    return mode === 'yes'
  }, [scope.kycConfig.enableConsentSources])

  const cascadeLabel = (id: string) => WORKFLOW_CASCADE_SOURCES.find((s) => s.id === id)?.label || id

  const cascadeSourcesAu = useMemo(() => WORKFLOW_CASCADE_SOURCES.filter((s) => s.region === 'au'), [])
  const cascadeSourcesInternational = useMemo(
    () => WORKFLOW_CASCADE_SOURCES.filter((s) => s.region === 'international'),
    []
  )

  // Check if a source is selected in KYC data sources
  const isSourceSelectedInKyc = (sourceId: string): boolean => {
    const selectedDataSources = scope.kycConfig.selectedDataSources || []
    return selectedDataSources.some(s => s.sourceId === sourceId)
  }

  // Add a source to KYC data sources if not already present
  const addSourceToKyc = (sourceId: string) => {
    const selectedDataSources = scope.kycConfig.selectedDataSources || []
    if (!selectedDataSources.some(s => s.sourceId === sourceId)) {
      updateKycConfig('selectedDataSources', [
        ...selectedDataSources,
        { sourceId: sourceId as any, via: 'frankieone', directNote: '' }
      ])
    }
  }

  const tabHeaderGroup = getGroupById('recipes_tab_header')
  const inheritedAmlGroup = getGroupById('recipes_inherited_aml_group')
  const workflowsGroup = getGroupById('recipes_workflows_group')
  const presetDropdownGroup = getGroupById('recipes_preset_dropdown_group')
  const emptyStateGroup = getGroupById('recipes_empty_state_group')

  const workflowNameField = getFieldById('recipes_workflow_name')
  const workflowIdField = getFieldById('recipes_workflow_id')
  const workflowUrlField = getFieldById('recipes_workflow_url')
  const workflowNotesField = getFieldById('recipes_workflow_notes')
  const associatedFlowField = getFieldById('recipes_associated_flow')
  const orderedChecksField = getFieldById('recipes_ordered_checks')
  const addCheckField = getFieldById('recipes_add_check')
  const useDefaultFlowField = getFieldById('recipes_use_default_flow')
  const noChecksYetField = getFieldById('recipes_no_checks_yet')
  const kycRulesetField = getFieldById('recipes_kyc_ruleset')
  const govIdRulesetHelperField = getFieldById('recipes_gov_id_ruleset_helper')
  const kycCascadeField = getFieldById('recipes_kyc_cascade')

  const idvRequirementField = getFieldById('recipes_idv_requirement')
  const idvFlowOverrideField = getFieldById('recipes_idv_flow_override')
  const idvUseWorkflowDefaultFlowField = getFieldById('recipes_idv_use_workflow_default_flow')
  const idvFlowDetailsField = getFieldById('recipes_idv_flow_details')

  const riskBranchesField = getFieldById('recipes_risk_branches')
  const riskAddBranchField = getFieldById('recipes_risk_add_branch')
  const riskBranchesEmptyField = getFieldById('recipes_risk_branches_empty')
  const riskBranchConditionField = getFieldById('recipes_risk_branch_condition')
  const riskBranchChecksField = getFieldById('recipes_risk_branch_checks')
  const riskBranchAddCheckField = getFieldById('recipes_risk_branch_add_check')
  const riskBranchNoChecksField = getFieldById('recipes_risk_branch_no_checks')

  return (
    <div className="space-y-6">
      <ResellerInheritedBanner />

      {/* Workflow Requirements */}
      <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-4 bg-[var(--bg-secondary)]">
        <h4 className="text-sm font-semibold text-[var(--text-primary)]">Workflow Requirements</h4>
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-2">
              Number of workflows required
            </label>
            <input
              type="number"
              min={0}
              className="w-full h-12 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              placeholder="—"
              value={scope.workflowsRequiredCount ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? null : parseInt(e.target.value, 10)
                updateWorkflowsMeta('workflowsRequiredCount', val)
              }}
              disabled={disabled}
            />
          </div>
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-2">Notes</label>
            <textarea
              className="w-full min-h-[80px] bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
              placeholder="General notes about workflow requirements, e.g. customer journey details, special considerations..."
              value={scope.workflowsNotes}
              onChange={(e) => updateWorkflowsMeta('workflowsNotes', e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      <div className="border-b border-[var(--border-color)] pb-4">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-medium text-[var(--text-primary)]">
              {tabHeaderGroup?.formTitle || 'Workflow Builder'}
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {tabHeaderGroup?.formDescription ||
                'Define ordered checks and associate IDV flows for different customer journeys'}
            </p>
          </div>
          {isV1 ? (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/30">
              V1 Platform — Recipe Mode
            </span>
          ) : (
            <div className="flex items-center gap-3">
              <button
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 whitespace-nowrap"
                onClick={addWorkflow}
                disabled={disabled}
              >
                <Plus size={18} />
                Add Workflow
              </button>
              <button
                className="flex items-center gap-2 px-4 py-2 border border-[var(--border-color)] rounded-lg text-sm font-medium text-[var(--text-primary)] hover:border-gray-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                onClick={addComplexWorkflow}
                disabled={disabled}
              >
                <Plus size={18} />
                Add Complex Workflow
              </button>
            </div>
          )}
        </div>

        {isV1 ? (
          <div className="flex items-center gap-3 flex-wrap">
            <select
              className="h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 min-w-0 max-w-xs"
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value
                if (v) {
                  const recipe = V1_RECIPES.find((r) => r.key === v)
                  if (recipe && !getV1RecipeDisabledReason(recipe, scope.amlConfig.level, v1RulesetKeys)) {
                    addV1RecipeWorkflow(v)
                  }
                }
                e.currentTarget.value = ''
              }}
              disabled={disabled}
            >
              <option value="">Select a preset profile…</option>
              {V1_RECIPES.map((r) => {
                const reason = getV1RecipeDisabledReason(r, scope.amlConfig.level, v1RulesetKeys)
                return (
                  <option key={r.key} value={r.key} disabled={!!reason}>
                    {r.label} — {r.checkTypes.join(', ')}{reason ? ` · ${reason}` : ''}
                  </option>
                )
              })}
            </select>
            <button
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 whitespace-nowrap"
              onClick={addV1CustomWorkflow}
              disabled={disabled}
            >
              <Plus size={18} />
              Custom Profile
            </button>
          </div>
        ) : (
          <div>
            <select
              className="h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value
                if (v === 'full' || v === 'simple' || v === 'step_up') {
                  const presetIncludesAml = v === 'full' || v === 'step_up'
                  if (presetIncludesAml && !amlStepAllowed) {
                    setAmlAddError('AML is set to N/A or not configured. Enable AML in the AML tab before adding an AML check to the workflow.')
                  } else {
                    setAmlAddError(null)
                    addPresetWorkflow(v)
                  }
                }
                e.currentTarget.value = ''
              }}
              disabled={disabled}
            >
              <option value="">{presetDropdownGroup?.helperText || 'Add preset…'}</option>
              <option value="full">Blocklist → Gov ID → KYC 2+2 → IDV/Biometrics → AML</option>
              <option value="step_up">Blocklist → Gov ID → KYC 2+2 → Risk (step-up to IDV/Biometrics) → AML</option>
              <option value="simple">KYC 1+1 (no Gov ID step)</option>
            </select>
          </div>
        )}
      </div>

      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">
          {inheritedAmlGroup?.formTitle || 'Inherited Flows, Rulesets and AML'}
        </h4>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        {/* IDV Flows */}
        <div>
          <div className="text-sm font-medium text-[var(--text-primary)] mb-1">IDV Flows</div>
          {idvEnabled ? (
            idvFlows.length > 0 ? (
              <div className="space-y-1">
                {idvFlows.map((flow, idx) => {
                  const enabledSteps = [
                    flow.consentScreenEnabled === 'yes' ? 'Consent' : null,
                    flow.idVerificationEnabled === 'yes' ? 'IDV' : null,
                    flow.biometricsEnabled === 'yes' ? 'Biometrics' : null,
                    flow.reviewScreenEnabled === 'yes' ? 'Review' : null,
                    flow.redirectPageEnabled === 'yes' ? 'Redirect' : null,
                    flow.customScreenEnabled === 'yes' ? `Custom${flow.customScreenDetails ? ` (${flow.customScreenDetails})` : ''}` : null,
                  ].filter(Boolean).join(' > ')

                  return (
                    <div key={flow.id || idx} className="text-sm text-[var(--text-secondary)] pl-3">
                      • {flow.name || flow.id || `Flow ${idx + 1}`}
                      {flow.vendor && flow.vendor !== 'unknown' ? ` (${flow.vendor})` : ''}
                      {enabledSteps && ` — ${enabledSteps}`}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-sm text-[var(--text-secondary)] pl-3">No flows defined</div>
            )
          ) : (
            <div className="text-sm text-[var(--text-secondary)] pl-3">
              ID Verification is set to {idvDisabledLabel}
            </div>
          )}
        </div>

        {/* KYC Rulesets */}
        <div>
          <div className="text-sm font-medium text-[var(--text-primary)] mb-1">KYC Rulesets</div>
          {scope.kycConfig.rulesets && scope.kycConfig.rulesets.length > 0 ? (
            <div className="space-y-1">
              {scope.kycConfig.rulesets.map((ruleset) => {
                const rulesetDef = KYC_RULESETS.find((r) => r.key === ruleset.rulesetKey)
                const name = ruleset.name || rulesetDef?.label || ruleset.rulesetKey
                return (
                  <div key={ruleset.id} className="text-sm text-[var(--text-secondary)] pl-3">
                    • {name}
                    {rulesetDef?.region ? ` (${rulesetDef.region})` : ''}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-sm text-[var(--text-secondary)] pl-3">No rulesets configured</div>
          )}
        </div>

        {/* Credit Header */}
        <div>
          <div className="text-sm font-medium text-[var(--text-primary)] mb-1">Credit Header</div>
          {creditHeaderAllowed ? (
            (() => {
              const equifax = scope.kycConfig.creditBureauSources?.equifaxCreditHeader
              const experian = scope.kycConfig.creditBureauSources?.experianCreditHeader
              const hasEquifax = equifax === 'yes'
              const hasExperian = experian === 'yes'
              const sources = []
              if (hasEquifax) sources.push('Equifax')
              if (hasExperian) sources.push('Experian')

              return (
                <div className="text-sm text-[var(--text-secondary)] pl-3">
                  {sources.length > 0
                    ? `• Yes, credit headers allowed: ${sources.join(', ')}`
                    : '• Yes, credit headers allowed (sources not yet configured)'}
                </div>
              )
            })()
          ) : (
            <div className="text-sm text-[var(--text-secondary)] pl-3">
              • Not enabled
            </div>
          )}
        </div>

        {/* KYC Data Sources */}
        <div>
          <div className="text-sm font-medium text-[var(--text-primary)] mb-1">KYC Data Sources</div>
          {(() => {
            const selectedDataSources = scope.kycConfig.selectedDataSources || []
            if (selectedDataSources.length === 0) {
              return (
                <div className="text-sm text-[var(--text-secondary)] pl-3">
                  No data sources selected yet
                </div>
              )
            }
            return (
              <div className="space-y-1">
                {selectedDataSources.map((ds, idx) => {
                  const source = WORKFLOW_CASCADE_SOURCES.find(s => s.id === ds.sourceId)
                  const label = source?.label || ds.sourceId
                  const via = ds.via === 'direct' ? 'Direct' : 'via FrankieOne'
                  return (
                    <div key={idx} className="text-sm text-[var(--text-secondary)] pl-3">
                      • {label} <span className="text-[var(--text-muted)]">({via})</span>
                    </div>
                  )
                })}
              </div>
            )
          })()}
        </div>

        {/* AML Settings */}
        <div>
          <div className="text-sm font-medium text-[var(--text-primary)] mb-1">AML Settings</div>
          {!amlStepAllowed ? (
            <div className="text-sm text-[var(--text-secondary)] pl-3">
              AML is set to {scope.amlConfig.level === 'na' ? 'N/A' : 'not configured'}. Enable AML in the AML tab to configure AML steps.
            </div>
          ) : (
            <div className="space-y-1">
              <div className="text-sm text-[var(--text-secondary)] pl-3">• Relationship: {amlRelationshipLabel}</div>
              <div className="text-sm text-[var(--text-secondary)] pl-3">• Vendor: {amlVendorLabel}</div>
              <div className="text-sm text-[var(--text-secondary)] pl-3">• Coverage: {amlCoverage}</div>
            </div>
          )}
        </div>
      </div>

      {amlAddError && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300">
          {amlAddError}
        </div>
      )}

      {workflowsConfig.length === 0 ? (
        <div className="border border-[var(--border-color)] rounded-lg p-6 text-sm text-[var(--text-secondary)]">
          {emptyStateGroup?.formTitle || 'No workflows yet. Click “Add Workflow” to create one.'}
        </div>
      ) : null}

      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">
          {workflowsGroup?.formTitle || 'Workflows'}
        </h4>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        {workflowsConfig.map((wf) => {
          if (wf.kind === 'complex') {
            return (
              <div key={wf.id} className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">
                        {workflowNameField?.formLabel || 'Workflow name'}
                      </label>
                      <input
                        type="text"
                        className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        value={wf.name}
                        onChange={(e) => updateWorkflow(wf.id, { name: e.target.value })}
                        disabled={disabled}
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">
                        {workflowIdField?.formLabel || 'Workflow ID'}
                      </label>
                      <input
                        type="text"
                        className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        value={wf.workflowId}
                        onChange={(e) => updateWorkflow(wf.id, { workflowId: e.target.value })}
                        disabled={disabled}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">
                        {workflowUrlField?.formLabel || 'Workflow URL'}
                      </label>
                      <input
                        type="url"
                        className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        value={wf.url}
                        onChange={(e) => updateWorkflow(wf.id, { url: e.target.value })}
                        disabled={disabled}
                        placeholder={workflowUrlField?.placeholder || 'https://...'}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 disabled:opacity-50"
                      onClick={() => removeWorkflow(wf.id)}
                      disabled={disabled}
                    >
                      <Trash2 size={16} />
                      Remove
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-2">
                    {workflowNotesField?.formLabel || 'Notes'}
                  </label>
                  <textarea
                    className="w-full min-h-[96px] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    value={wf.notes}
                    onChange={(e) => updateWorkflow(wf.id, { notes: e.target.value })}
                    disabled={disabled}
                    placeholder={workflowNotesField?.placeholder || 'Capture the detailed workflow logic here.'}
                  />
                </div>
              </div>
            )
          }

          const hasGovIdStep = wf.steps.some((s) => s.type === 'gov_id')
          const isV1Recipe = !!(wf as BuilderWorkflow).v1RecipeKey
          const v1Recipe = isV1Recipe ? V1_RECIPES.find((r) => r.key === (wf as BuilderWorkflow).v1RecipeKey) : null
          const v1RecipeConflict = v1Recipe ? getV1RecipeDisabledReason(v1Recipe, scope.amlConfig.level, v1RulesetKeys) : null
          const isV1Custom = !isV1Recipe && Array.isArray((wf as BuilderWorkflow).v1CustomChecks)
          const v1CustomChecks: V1CustomCheck[] = isV1Custom ? ((wf as BuilderWorkflow).v1CustomChecks || []) : []
          return (
            <div key={wf.id} className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
              {isV1Custom ? (
                <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-teal-300">Custom V1 Profile</span>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-teal-400/50 mb-2">Check Types (ordered)</div>
                    {v1CustomChecks.length === 0 ? (
                      <div className="text-xs text-[var(--text-muted)]">No checks added yet. Use the dropdown below to add check types.</div>
                    ) : (
                      <div className="space-y-1.5">
                        {v1CustomChecks.map((check, ci) => {
                          const checkDef = V1_CHECK_TYPE_OPTIONS.find((o) => o.id === check.checkTypeId)
                          return (
                            <div
                              key={check.id}
                              className="flex items-center gap-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 py-2"
                              draggable={!disabled}
                              onDragStart={(e) => {
                                e.dataTransfer.setData('text/plain', check.id)
                                e.dataTransfer.effectAllowed = 'move'
                              }}
                              onDragOver={(e) => {
                                e.preventDefault()
                                e.dataTransfer.dropEffect = 'move'
                              }}
                              onDrop={(e) => {
                                e.preventDefault()
                                const fromId = e.dataTransfer.getData('text/plain')
                                if (fromId === check.id) return
                                const fromIdx = v1CustomChecks.findIndex((c) => c.id === fromId)
                                if (fromIdx === -1) return
                                const next = [...v1CustomChecks]
                                const [moved] = next.splice(fromIdx, 1)
                                next.splice(ci, 0, moved)
                                updateWorkflow(wf.id, { v1CustomChecks: next })
                              }}
                            >
                              <GripVertical size={14} className="text-[var(--text-muted)] cursor-grab shrink-0" />
                              <span className="text-xs text-[var(--text-muted)] w-5 shrink-0">{ci + 1}.</span>
                              <span className="text-sm text-[var(--text-primary)] font-medium flex-1">{checkDef?.label || check.checkTypeId}</span>
                              {checkDef?.description && (
                                <span className="text-[10px] text-[var(--text-muted)] hidden md:inline max-w-[200px] truncate" title={checkDef.description}>
                                  {checkDef.description}
                                </span>
                              )}
                              <label className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)] cursor-pointer whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  className="accent-red-500"
                                  checked={check.stopOnFail}
                                  onChange={(e) => {
                                    const next = v1CustomChecks.map((c) => c.id === check.id ? { ...c, stopOnFail: e.target.checked } : c)
                                    updateWorkflow(wf.id, { v1CustomChecks: next })
                                  }}
                                  disabled={disabled}
                                />
                                Stop on fail
                              </label>
                              <label className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)] cursor-pointer whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  className="accent-amber-500"
                                  checked={check.optional}
                                  onChange={(e) => {
                                    const next = v1CustomChecks.map((c) => c.id === check.id ? { ...c, optional: e.target.checked } : c)
                                    updateWorkflow(wf.id, { v1CustomChecks: next })
                                  }}
                                  disabled={disabled}
                                />
                                Optional
                              </label>
                              <button
                                type="button"
                                className="text-red-400 hover:text-red-300 disabled:opacity-50 shrink-0"
                                onClick={() => {
                                  const next = v1CustomChecks.filter((c) => c.id !== check.id)
                                  updateWorkflow(wf.id, { v1CustomChecks: next })
                                }}
                                disabled={disabled}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    <div className="mt-2">
                      <select
                        className="h-9 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-xs text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        defaultValue=""
                        onChange={(e) => {
                          const checkTypeId = e.target.value
                          if (!checkTypeId) return
                          const newCheck: V1CustomCheck = {
                            id: `v1check_${Date.now()}_${Math.random().toString(16).slice(2)}`,
                            checkTypeId,
                            stopOnFail: false,
                            optional: false,
                          }
                          updateWorkflow(wf.id, { v1CustomChecks: [...v1CustomChecks, newCheck] })
                          e.currentTarget.value = ''
                        }}
                        disabled={disabled}
                      >
                        <option value="">Add check type…</option>
                        {V1_CHECK_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ) : null}

              {isV1Recipe && v1Recipe ? (
                <div className="space-y-2">
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-white">{v1Recipe.label}</span>
                      <span className="text-xs text-amber-400/40 font-mono">{v1Recipe.key}</span>
                      {v1Recipe.amlLevel === 'enhanced' && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-500/15 text-purple-400 border border-purple-500/30">
                          Enhanced AML (Media)
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-amber-400/30 mb-1">Check Types</div>
                        <div className="flex flex-wrap gap-1.5">
                          {v1Recipe.checkTypes.map((ct) => (
                            <span key={ct} className="px-2 py-0.5 rounded text-[11px] bg-amber-500/5 text-amber-400/50 border border-amber-500/10">
                              {ct}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-amber-400/30 mb-1">Check Order</div>
                        <div className="text-xs text-amber-400/40">
                          {v1Recipe.checkOrder.map((co, i) => (
                            <span key={co}>
                              {i > 0 && <span className="mx-1.5 text-amber-500/20">&rarr;</span>}
                              <span>{i + 1}. {co}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {v1RecipeConflict && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
                      ⚠ Configuration mismatch — {v1RecipeConflict}
                    </div>
                  )}
                </div>
              ) : null}

              {!idvEnabled && !isV1Recipe ? (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300">
                  ID Verification is set to {idvDisabledLabel}. Flow selection is disabled until ID Verification is enabled.
                </div>
              ) : null}

              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[var(--text-secondary)] mb-2">
                      {workflowNameField?.formLabel || 'Workflow name'}
                    </label>
                    <input
                      type="text"
                      className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                      value={wf.name}
                      onChange={(e) => updateWorkflow(wf.id, { name: e.target.value })}
                      disabled={disabled || isV1Recipe}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[var(--text-secondary)] mb-2">
                      {associatedFlowField?.formLabel || 'Associated Flow (optional)'}
                    </label>
                    <select
                      className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                      value={wf.associatedFlowId || ''}
                      onChange={(e) => updateWorkflow(wf.id, { associatedFlowId: e.target.value || null })}
                      disabled={disabled || !idvEnabled}
                    >
                      <option value="">{useDefaultFlowField?.formLabel || 'Use default flow'}</option>
                      {idvFlows.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                    {!idvEnabled ? (
                      <div className="text-xs text-[var(--text-muted)] mt-1">
                        ID Verification is set to {idvDisabledLabel}. Enable it in the ID Verification tab to select flows.
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 disabled:opacity-50"
                    onClick={() => removeWorkflow(wf.id)}
                    disabled={disabled}
                  >
                    <Trash2 size={16} />
                    Remove
                  </button>
                </div>
              </div>

              {!isV1Custom && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm font-medium text-[var(--text-primary)]">
                    {orderedChecksField?.formLabel || 'Ordered checks'}
                    {isV1Recipe && (
                      <span className="ml-2 text-xs text-[var(--text-muted)]">(locked — defined by recipe)</span>
                    )}
                  </div>
                  {!isV1Recipe && (
                    <div className="flex items-center gap-2">
                      <select
                        className="h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        defaultValue=""
                        onChange={(e) => {
                          const v = e.target.value as WorkflowStepType
                          if (!v) return
                          if (v === 'aml' && !amlStepAllowed) {
                            setAmlAddError('AML is set to N/A or not configured. Enable AML in the AML tab before adding an AML check to the workflow.')
                            e.currentTarget.value = ''
                            return
                          }
                          setAmlAddError(null)
                          addWorkflowStep(wf.id, v)
                          e.currentTarget.value = ''
                        }}
                        disabled={disabled}
                      >
                        <option value="">{addCheckField?.formLabel || 'Add check…'}</option>
                        {WORKFLOW_STEP_OPTIONS.map((o) => (
                          <option key={o.id} value={o.id} disabled={o.id === 'aml' && !amlStepAllowed}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {wf.steps.length === 0 ? (
                  <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 text-sm text-[var(--text-secondary)]">
                    {noChecksYetField?.formLabel || 'No checks yet. Use “Add check…” to add steps.'}
                  </div>
                ) : null}

                <div className="space-y-3">
                  {wf.steps.map((s, idx) => {
                    const commonHeader = (
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-medium text-[var(--text-primary)]">
                          {idx + 1}. {stepLabel(s.type)}
                        </div>
                        {!isV1Recipe && (
                        <div className="flex items-center gap-2">
                          <div className="text-xs text-[var(--text-muted)] select-none">Drag to reorder</div>
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 px-2 py-1 text-xs border border-red-500/30 rounded text-red-400 hover:text-red-300 disabled:opacity-50"
                            onClick={() => removeWorkflowStep(wf.id, s.id)}
                            disabled={disabled}
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                        </div>
                        )}
                      </div>
                    )

                    return (
                      <div
                        key={s.id}
                        className={`bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 space-y-3 ${
                          draggingStepId === s.id ? 'opacity-70' : ''
                        }`}
                        draggable={!disabled && !isV1Recipe}
                        onDragStart={(e) => {
                          setDraggingStepId(s.id)
                          e.dataTransfer.setData('text/plain', s.id)
                          e.dataTransfer.effectAllowed = 'move'
                        }}
                        onDragEnd={() => setDraggingStepId(null)}
                        onDragOver={(e) => {
                          if (disabled) return
                          e.preventDefault()
                          e.dataTransfer.dropEffect = 'move'
                        }}
                        onDrop={(e) => {
                          if (disabled) return
                          e.preventDefault()
                          const fromId = e.dataTransfer.getData('text/plain')
                          if (!fromId) return
                          reorderWorkflowStep(wf.id, fromId, idx)
                          setDraggingStepId(null)
                        }}
                      >
                        {commonHeader}

                        {s.type === 'kyc' ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                                {kycRulesetField?.formLabel || 'Ruleset'}
                              </label>
                              <select
                                className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                value={s.rulesetId}
                                onChange={(e) => {
                                  const selectedKey = e.target.value
                                  updateWorkflowStep(wf.id, s.id, { rulesetId: selectedKey })

                                  // Auto-add ruleset to KYC tab if not already there
                                  if (selectedKey && selectedKey !== '' && selectedKey !== 'unknown') {
                                    const existsInKyc = scope.kycConfig.rulesets.some(r => r.rulesetKey === selectedKey)
                                    if (!existsInKyc) {
                                      addKycRuleset(selectedKey)
                                    }
                                  }
                                }}
                                disabled={disabled}
                              >
                                <option value="">{kycRulesetField?.placeholder || 'Select ruleset'}</option>

                                {/* Show configured rulesets from KYC tab first */}
                                {scope.kycConfig.rulesets.length > 0 && scope.kycConfig.rulesets.some(r => r.rulesetKey && r.rulesetKey !== 'unknown') && (
                                  <optgroup label="Configured on KYC Tab">
                                    {scope.kycConfig.rulesets
                                      .filter(r => r.rulesetKey && r.rulesetKey !== 'unknown')
                                      .map((r) => {
                                        const ruleDef = KYC_RULESETS.find(rd => rd.key === r.rulesetKey)
                                        const label = r.name || (ruleDef?.label) || r.rulesetKey
                                        const isDisabled = hasGovIdStep && r.rulesetKey.includes('no_gov_id')
                                        return (
                                          <option key={r.id} value={r.rulesetKey} disabled={isDisabled}>
                                            {label}
                                          </option>
                                        )
                                      })}
                                  </optgroup>
                                )}

                                {/* Show all available rulesets */}
                                <optgroup label="All Available Rulesets">
                                  {KYC_RULESETS.map((r) => (
                                    <option key={r.key} value={r.key} disabled={hasGovIdStep && r.key.includes('no_gov_id')}>
                                      {r.label}
                                    </option>
                                  ))}
                                </optgroup>
                              </select>
                              {hasGovIdStep ? (
                                <div className="text-xs text-[var(--text-muted)] mt-1">
                                  {govIdRulesetHelperField?.formLabel ||
                                    'Gov ID check is present, so “no Gov ID” rulesets are disabled.'}
                                </div>
                              ) : null}
                            </div>

                            <div>
                              <label className="flex items-center gap-1.5 text-sm text-[var(--text-secondary)] mb-2">
                                <span>{kycCascadeField?.formLabel || 'Cascade (ordered)'}</span>
                                <Tooltip
                                  content={
                                    <div>
                                      <div className="font-semibold mb-1">Recommended order</div>
                                      <div>DVS → Experian → Equifax → Superchoice</div>
                                    </div>
                                  }
                                >
                                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                                </Tooltip>
                              </label>
                              <div className="space-y-3">
                                <select
                                  className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                  value=""
                                  onChange={(e) => {
                                    const v = e.target.value as any
                                    if (!v) return
                                    if (s.cascadeSources.includes(v)) return
                                    updateWorkflowStep(wf.id, s.id, { cascadeSources: [...s.cascadeSources, v] })
                                    // Also add to KYC data sources
                                    addSourceToKyc(v)
                                  }}
                                  disabled={disabled}
                                >
                                  <option value="">{kycCascadeField?.placeholder || 'Add source…'}</option>
                                  <optgroup label="Australia">
                                    {cascadeSourcesAu.map((src) => {
                                      const alreadySelected = s.cascadeSources.includes(src.id)
                                      const creditHeaderDisabled = Boolean(src.creditHeader) && !creditHeaderAllowed
                                      const isInKyc = isSourceSelectedInKyc(src.id)
                                      let prefix = ''
                                      if (isInKyc) {
                                        prefix = '✓ '  // Green checkmark for sources in KYC
                                      } else if (src.creditHeader && !creditHeaderAllowed) {
                                        prefix = '○ '  // Grey circle for disabled credit headers
                                      }
                                      return (
                                        <option
                                          key={src.id}
                                          value={src.id}
                                          disabled={alreadySelected || creditHeaderDisabled}
                                          title={src.details}
                                          style={{
                                            color: isInKyc ? '#10b981' : (creditHeaderDisabled ? '#6b7280' : 'inherit')
                                          }}
                                        >
                                          {prefix}{src.label}
                                          {creditHeaderDisabled ? ' (Reporting Entity only)' : ''}
                                        </option>
                                      )
                                    })}
                                  </optgroup>
                                  <optgroup label="International">
                                    {cascadeSourcesInternational.map((src) => {
                                      const alreadySelected = s.cascadeSources.includes(src.id)
                                      const creditHeaderDisabled = Boolean(src.creditHeader) && !creditHeaderAllowed
                                      const isInKyc = isSourceSelectedInKyc(src.id)
                                      let prefix = ''
                                      if (isInKyc) {
                                        prefix = '✓ '  // Green checkmark for sources in KYC
                                      } else if (src.creditHeader && !creditHeaderAllowed) {
                                        prefix = '○ '  // Grey circle for disabled credit headers
                                      }
                                      return (
                                        <option
                                          key={src.id}
                                          value={src.id}
                                          disabled={alreadySelected || creditHeaderDisabled}
                                          title={src.details}
                                          style={{
                                            color: isInKyc ? '#10b981' : (creditHeaderDisabled ? '#6b7280' : 'inherit')
                                          }}
                                        >
                                          {prefix}{src.label}
                                          {creditHeaderDisabled ? ' (Reporting Entity only)' : ''}
                                        </option>
                                      )
                                    })}
                                  </optgroup>
                                </select>

                                <div className="space-y-2">
                                  {s.cascadeSources.map((srcId, cIdx) => (
                                    <div
                                      key={srcId}
                                      className={`flex items-center justify-between gap-2 rounded border border-[var(--border-color)] bg-[var(--bg-tertiary)] px-2 py-1.5 ${
                                        draggingCascadeId === srcId ? 'opacity-70' : ''
                                      }`}
                                      draggable={!disabled}
                                      onDragStart={(e) => {
                                        setDraggingCascadeId(srcId)
                                        e.dataTransfer.setData('text/plain', srcId)
                                        e.dataTransfer.effectAllowed = 'move'
                                      }}
                                      onDragEnd={() => setDraggingCascadeId(null)}
                                      onDragOver={(e) => {
                                        if (disabled) return
                                        e.preventDefault()
                                        e.dataTransfer.dropEffect = 'move'
                                      }}
                                      onDrop={(e) => {
                                        if (disabled) return
                                        e.preventDefault()
                                        const fromId = e.dataTransfer.getData('text/plain')
                                        if (!fromId || fromId === srcId) return
                                        const fromIdx = s.cascadeSources.findIndex((x) => x === fromId)
                                        if (fromIdx < 0) return
                                        const next = [...s.cascadeSources]
                                        next.splice(fromIdx, 1)
                                        next.splice(cIdx, 0, fromId as any)
                                        updateWorkflowStep(wf.id, s.id, { cascadeSources: next })
                                        setDraggingCascadeId(null)
                                      }}
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <GripVertical size={14} className="text-[var(--text-muted)]" />
                                        <div className="text-sm text-[var(--text-primary)] truncate">{cascadeLabel(srcId)}</div>
                                      </div>
                                      <button
                                        type="button"
                                        className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                                        onClick={() => updateWorkflowStep(wf.id, s.id, { cascadeSources: s.cascadeSources.filter((x) => x !== srcId) })}
                                        disabled={disabled}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  ))}
                                </div>

                                {!creditHeaderAllowed ? (
                                  <div className="text-xs text-[var(--text-muted)]">
                                    Credit Header sources require Reporting Entity to be set to Yes and credit header checks enabled in the KYC tab.
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ) : null}

                        {s.type === 'idv_biometrics' ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                                {idvRequirementField?.formLabel || 'Requirement'}
                              </label>
                              <select
                                className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                value={s.requirement}
                                onChange={(e) =>
                                  updateWorkflowStep(wf.id, s.id, {
                                    requirement: e.target.value === 'optional' ? 'optional' : 'mandatory',
                                  })
                                }
                                disabled={disabled}
                              >
                                <option value="mandatory">
                                  {idvRequirementField?.options?.find((o) => o.value === 'mandatory')?.label || 'Mandatory'}
                                </option>
                                <option value="optional">
                                  {idvRequirementField?.options?.find((o) => o.value === 'optional')?.label || 'Optional'}
                                </option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                                {idvFlowOverrideField?.formLabel || 'Flow override (optional)'}
                              </label>
                              <select
                                className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                value={s.flowId || ''}
                                onChange={(e) => updateWorkflowStep(wf.id, s.id, { flowId: e.target.value || null })}
                                disabled={disabled || !idvEnabled}
                              >
                                <option value="">
                                  {idvUseWorkflowDefaultFlowField?.formLabel || 'Use workflow/default flow'}
                                </option>
                                {idvFlows.map((f) => (
                                  <option key={f.id} value={f.id}>
                                    {f.name}
                                  </option>
                                ))}
                              </select>
                              {!idvEnabled ? (
                                <div className="text-xs text-[var(--text-muted)] mt-1">
                                  ID Verification is set to {idvDisabledLabel}. Enable it to select a flow.
                                </div>
                              ) : null}
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                                {idvFlowDetailsField?.formLabel || 'Flow details'}
                              </label>
                              {(() => {
                                const d = formatIdvFlowSummary(s, scope, wf.associatedFlowId)
                                if (!('flowName' in d)) {
                                  return <div className="h-10 flex items-center text-sm text-[var(--text-secondary)]">{d.summary}</div>
                                }
                                return (
                                  <div className="text-sm text-[var(--text-secondary)] space-y-1">
                                    <div className="text-[var(--text-primary)] font-medium truncate">{d.flowName}</div>
                                    <div className="text-xs text-[var(--text-muted)]">Steps: {d.flowSteps}</div>
                                    <div className="text-xs text-[var(--text-muted)]">Vendor: {d.flowVendor || '—'}</div>
                                    <div className="text-xs text-[var(--text-muted)]">Docs: {d.flowDocs}</div>
                                  </div>
                                )
                              })()}
                            </div>
                          </div>
                        ) : null}

                        {s.type === 'aml' ? (
                          <div className="space-y-2">
                            {!amlConfigured ? (
                              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300">
                                AML is not configured yet. Set the AML level and vendor settings in the AML tab.
                              </div>
                            ) : null}
                            <div className="text-sm text-[var(--text-secondary)]">Inherits AML vendor + coverage from AML tab.</div>
                          </div>
                        ) : null}

                        {s.type === 'duplicate' ? (
                          <div className="space-y-2">
                            <label className="block text-xs text-[var(--text-secondary)]">
                              What will this duplicate check entail?
                            </label>
                            <textarea
                              className="w-full h-20 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                              value={s.notes}
                              onChange={(e) => updateWorkflowStep(wf.id, s.id, { notes: e.target.value })}
                              disabled={disabled}
                              placeholder="e.g. Match on full name + DOB, exact match, medium strength…"
                            />
                          </div>
                        ) : null}

                        {s.type === 'risk' ? (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-4">
                              <div className="text-sm font-medium text-[var(--text-primary)]">
                                {riskBranchesField?.formLabel || 'Branches'}
                              </div>
                              <button
                                type="button"
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[var(--border-color)] rounded hover:border-gray-600 text-[var(--text-secondary)] disabled:opacity-50"
                                onClick={() => updateWorkflowStep(wf.id, s.id, { branches: [...s.branches, createBranch()] })}
                                disabled={disabled}
                              >
                                <Plus size={14} />
                                {riskAddBranchField?.formLabel || 'Add branch'}
                              </button>
                            </div>

                            {s.branches.length === 0 ? (
                              <div className="text-sm text-[var(--text-secondary)]">
                                {riskBranchesEmptyField?.formLabel ||
                                  'Add a branch to capture International cascade changes or step-up checks (AML/Fraud/Gov ID/KYC).'}
                              </div>
                            ) : null}

                            <div className="space-y-3">
                              {s.branches.map((b) => {
                                const hasGovIdInBranch = hasGovIdStep || b.steps.some((st) => st.type === 'gov_id')
                                const riskIndex = wf.steps.findIndex((st) => st.id === s.id)
                                const hasGovIdUpstream = wf.steps.slice(0, Math.max(0, riskIndex)).some((st) => st.type === 'gov_id')
                                const branchStepOptions = WORKFLOW_STEP_OPTIONS.filter((o) => o.id !== 'risk').filter((o) => {
                                  if (o.id !== 'gov_id') return true
                                  return !hasGovIdUpstream
                                })
                                return (
                                  <div key={b.id} className="border border-[var(--border-color)] rounded-lg p-3 bg-[var(--bg-tertiary)] space-y-3">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex-1">
                                        <label className="block text-xs text-[var(--text-secondary)] mb-1">
                                          {riskBranchConditionField?.formLabel || 'Condition / trigger'}
                                        </label>
                                        <input
                                          type="text"
                                          className="w-full h-9 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                          value={b.condition}
                                          onChange={(e) => {
                                            const next = s.branches.map((x) => (x.id === b.id ? { ...x, condition: e.target.value } : x))
                                            updateWorkflowStep(wf.id, s.id, { branches: next })
                                          }}
                                          disabled={disabled}
                                          placeholder={
                                            riskBranchConditionField?.placeholder || 'e.g. doc_country != AU OR risk_score > threshold'
                                          }
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        className="inline-flex items-center gap-2 px-2 py-1 text-xs border border-red-500/30 rounded text-red-400 hover:text-red-300 disabled:opacity-50"
                                        onClick={() => {
                                          const next = s.branches.filter((x) => x.id !== b.id)
                                          updateWorkflowStep(wf.id, s.id, { branches: next })
                                        }}
                                        disabled={disabled}
                                      >
                                        <Trash2 size={14} />
                                        Remove branch
                                      </button>
                                    </div>

                                    {/* Outcome selector */}
                                    <div className="flex items-center gap-3">
                                      <div className="text-xs text-[var(--text-muted)]">Outcome</div>
                                      <div className="flex items-center gap-1">
                                        {([
                                          { value: 'pass', label: 'Pass', color: 'bg-green-900/30 text-green-300 border-green-700/40' },
                                          { value: 'fail', label: 'Fail', color: 'bg-red-900/30 text-red-300 border-red-700/40' },
                                          { value: 'in_review', label: 'In Review', color: 'bg-amber-900/30 text-amber-300 border-amber-700/40' },
                                        ] as const).map((opt) => (
                                          <button
                                            key={opt.value}
                                            type="button"
                                            className={`px-2 py-1 text-[10px] font-medium rounded border transition-colors disabled:opacity-50 ${
                                              b.outcome === opt.value
                                                ? opt.color
                                                : 'border-[var(--border-color)] text-[var(--text-muted)] hover:border-gray-500'
                                            }`}
                                            onClick={() => {
                                              const newOutcome = (b.outcome === opt.value ? '' : opt.value) as RiskBranchOutcome
                                              const next = s.branches.map((x) => (x.id === b.id ? { ...x, outcome: newOutcome } : x))
                                              updateWorkflowStep(wf.id, s.id, { branches: next })
                                            }}
                                            disabled={disabled}
                                          >
                                            {opt.label}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    <div className="flex items-center justify-between gap-3">
                                      <div className="text-xs text-[var(--text-muted)]">
                                        {riskBranchChecksField?.formLabel || 'Checks to run for this branch'}
                                      </div>
                                      <select
                                        className="h-9 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-xs text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                        defaultValue=""
                                        onChange={(e) => {
                                          const v = e.target.value
                                          if (!v || !isStepType(v)) return
                                          const nextBranches = s.branches.map((x) =>
                                            x.id === b.id ? { ...x, steps: [...x.steps, createStep(v)] } : x
                                          )
                                          updateWorkflowStep(wf.id, s.id, { branches: nextBranches })
                                          e.currentTarget.value = ''
                                        }}
                                        disabled={disabled}
                                      >
                                        <option value="">{riskBranchAddCheckField?.formLabel || 'Add check…'}</option>
                                        {branchStepOptions.map((o) => (
                                          <option key={o.id} value={o.id}>
                                            {o.label}
                                          </option>
                                        ))}
                                      </select>
                                      {hasGovIdUpstream ? (
                                        <div className="text-xs text-[var(--text-muted)]">Gov ID already exists upstream; it can’t be added again inside a branch.</div>
                                      ) : null}
                                    </div>

                                    {b.steps.length === 0 ? (
                                      <div className="text-sm text-[var(--text-secondary)]">
                                        {riskBranchNoChecksField?.formLabel || 'No checks yet for this branch.'}
                                      </div>
                                    ) : null}

                                    <div className="space-y-3">
                                      {b.steps.map((bs) => {
                                        return (
                                          <div key={bs.id} className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3 space-y-3">
                                            <div className="flex items-center justify-between gap-3">
                                              <div className="text-sm font-medium text-[var(--text-primary)]">{stepLabel(bs.type)}</div>
                                              <button
                                                type="button"
                                                className="inline-flex items-center gap-2 px-2 py-1 text-xs border border-red-500/30 rounded text-red-400 hover:text-red-300 disabled:opacity-50"
                                                onClick={() => {
                                                  const nextBranches = s.branches.map((x) =>
                                                    x.id === b.id ? { ...x, steps: x.steps.filter((st) => st.id !== bs.id) } : x
                                                  )
                                                  updateWorkflowStep(wf.id, s.id, { branches: nextBranches })
                                                }}
                                                disabled={disabled}
                                              >
                                                <Trash2 size={14} />
                                                Remove
                                              </button>
                                            </div>

                                            {bs.type === 'kyc' ? (
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                  <label className="block text-sm text-[var(--text-secondary)] mb-2">
                                                    {kycRulesetField?.formLabel || 'Ruleset'}
                                                  </label>
                                                  <select
                                                    className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                                    value={bs.rulesetId}
                                                    onChange={(e) => {
                                                      const selectedKey = e.target.value
                                                      const nextBranches = s.branches.map((x) =>
                                                        x.id === b.id
                                                          ? {
                                                              ...x,
                                                              steps: x.steps.map((st) =>
                                                                st.id === bs.id && st.type === 'kyc' ? { ...st, rulesetId: selectedKey } : st
                                                              ),
                                                            }
                                                          : x
                                                      )
                                                      updateWorkflowStep(wf.id, s.id, { branches: nextBranches })

                                                      // Auto-add ruleset to KYC tab if not already there
                                                      if (selectedKey && selectedKey !== '' && selectedKey !== 'unknown') {
                                                        const existsInKyc = scope.kycConfig.rulesets.some(r => r.rulesetKey === selectedKey)
                                                        if (!existsInKyc) {
                                                          addKycRuleset(selectedKey)
                                                        }
                                                      }
                                                    }}
                                                    disabled={disabled}
                                                  >
                                                    <option value="">{kycRulesetField?.placeholder || 'Select ruleset'}</option>

                                                    {/* Show configured rulesets from KYC tab first */}
                                                    {scope.kycConfig.rulesets.length > 0 && scope.kycConfig.rulesets.some(r => r.rulesetKey && r.rulesetKey !== 'unknown') && (
                                                      <optgroup label="Configured on KYC Tab">
                                                        {scope.kycConfig.rulesets
                                                          .filter(r => r.rulesetKey && r.rulesetKey !== 'unknown')
                                                          .map((r) => {
                                                            const ruleDef = KYC_RULESETS.find(rd => rd.key === r.rulesetKey)
                                                            const label = r.name || (ruleDef?.label) || r.rulesetKey
                                                            const isDisabled = hasGovIdInBranch && r.rulesetKey.includes('no_gov_id')
                                                            return (
                                                              <option key={r.id} value={r.rulesetKey} disabled={isDisabled}>
                                                                {label}
                                                              </option>
                                                            )
                                                          })}
                                                      </optgroup>
                                                    )}

                                                    {/* Show all available rulesets */}
                                                    <optgroup label="All Available Rulesets">
                                                      {KYC_RULESETS.map((r) => (
                                                        <option
                                                          key={r.key}
                                                          value={r.key}
                                                          disabled={hasGovIdInBranch && r.key.includes('no_gov_id')}
                                                        >
                                                          {r.label}
                                                        </option>
                                                      ))}
                                                    </optgroup>
                                                  </select>
                                                  {hasGovIdInBranch ? (
                                                    <div className="text-xs text-[var(--text-muted)] mt-1">
                                                      {govIdRulesetHelperField?.formLabel ||
                                                        'Gov ID check is present, so “no Gov ID” rulesets are disabled.'}
                                                    </div>
                                                  ) : null}
                                                </div>

                                                <div>
                                                  <label className="flex items-center gap-1.5 text-sm text-[var(--text-secondary)] mb-2">
                                                    <span>{kycCascadeField?.formLabel || 'Cascade (ordered)'}</span>
                                                    <Tooltip
                                                      content={
                                                        <div>
                                                          <div className="font-semibold mb-1">Recommended order</div>
                                                          <div>DVS → Experian → Equifax → Superchoice</div>
                                                        </div>
                                                      }
                                                    >
                                                      <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                                                    </Tooltip>
                                                  </label>
                                                  <div className="space-y-3">
                                                    <select
                                                      className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                                      value=""
                                                      onChange={(e) => {
                                                        const v = e.target.value as any
                                                        if (!v) return
                                                        if (bs.cascadeSources.includes(v)) return
                                                        const nextCascade = [...bs.cascadeSources, v]
                                                        const nextBranches = s.branches.map((x) =>
                                                          x.id === b.id
                                                            ? {
                                                                ...x,
                                                                steps: x.steps.map((st) =>
                                                                  st.id === bs.id && st.type === 'kyc'
                                                                    ? { ...st, cascadeSources: nextCascade }
                                                                    : st
                                                                ),
                                                              }
                                                            : x
                                                        )
                                                        updateWorkflowStep(wf.id, s.id, { branches: nextBranches })
                                                        // Also add to KYC data sources
                                                        addSourceToKyc(v)
                                                      }}
                                                      disabled={disabled}
                                                    >
                                                      <option value="">{kycCascadeField?.placeholder || 'Add source…'}</option>
                                                      <optgroup label="Australia">
                                                        {cascadeSourcesAu.map((src) => {
                                                          const alreadySelected = bs.cascadeSources.includes(src.id)
                                                          const creditHeaderDisabled = Boolean(src.creditHeader) && !creditHeaderAllowed
                                                          const isInKyc = isSourceSelectedInKyc(src.id)
                                                          let prefix = ''
                                                          if (isInKyc) {
                                                            prefix = '✓ '  // Green checkmark for sources in KYC
                                                          } else if (src.creditHeader && !creditHeaderAllowed) {
                                                            prefix = '○ '  // Grey circle for disabled credit headers
                                                          }
                                                          return (
                                                            <option
                                                              key={src.id}
                                                              value={src.id}
                                                              disabled={alreadySelected || creditHeaderDisabled}
                                                              title={src.details}
                                                              style={{
                                                                color: isInKyc ? '#10b981' : (creditHeaderDisabled ? '#6b7280' : 'inherit')
                                                              }}
                                                            >
                                                              {prefix}{src.label}
                                                              {creditHeaderDisabled ? ' (Reporting Entity only)' : ''}
                                                            </option>
                                                          )
                                                        })}
                                                      </optgroup>
                                                      <optgroup label="International">
                                                        {cascadeSourcesInternational.map((src) => {
                                                          const alreadySelected = bs.cascadeSources.includes(src.id)
                                                          const creditHeaderDisabled = Boolean(src.creditHeader) && !creditHeaderAllowed
                                                          const isInKyc = isSourceSelectedInKyc(src.id)
                                                          let prefix = ''
                                                          if (isInKyc) {
                                                            prefix = '✓ '  // Green checkmark for sources in KYC
                                                          } else if (src.creditHeader && !creditHeaderAllowed) {
                                                            prefix = '○ '  // Grey circle for disabled credit headers
                                                          }
                                                          return (
                                                            <option
                                                              key={src.id}
                                                              value={src.id}
                                                              disabled={alreadySelected || creditHeaderDisabled}
                                                              title={src.details}
                                                              style={{
                                                                color: isInKyc ? '#10b981' : (creditHeaderDisabled ? '#6b7280' : 'inherit')
                                                              }}
                                                            >
                                                              {prefix}{src.label}
                                                              {creditHeaderDisabled ? ' (Reporting Entity only)' : ''}
                                                            </option>
                                                          )
                                                        })}
                                                      </optgroup>
                                                    </select>

                                                    <div className="space-y-2">
                                                      {bs.cascadeSources.map((srcId, cIdx) => (
                                                        <div
                                                          key={srcId}
                                                          className={`flex items-center justify-between gap-2 rounded border border-[var(--border-color)] bg-[var(--bg-tertiary)] px-2 py-1.5 ${
                                                            draggingCascadeId === srcId ? 'opacity-70' : ''
                                                          }`}
                                                          draggable={!disabled}
                                                          onDragStart={(e) => {
                                                            setDraggingCascadeId(srcId)
                                                            e.dataTransfer.setData('text/plain', srcId)
                                                            e.dataTransfer.effectAllowed = 'move'
                                                          }}
                                                          onDragEnd={() => setDraggingCascadeId(null)}
                                                          onDragOver={(e) => {
                                                            if (disabled) return
                                                            e.preventDefault()
                                                            e.dataTransfer.dropEffect = 'move'
                                                          }}
                                                          onDrop={(e) => {
                                                            if (disabled) return
                                                            e.preventDefault()
                                                            const fromId = e.dataTransfer.getData('text/plain')
                                                            if (!fromId || fromId === srcId) return
                                                            const fromIdx = bs.cascadeSources.findIndex((x) => x === fromId)
                                                            if (fromIdx < 0) return
                                                            const nextCascade = [...bs.cascadeSources]
                                                            nextCascade.splice(fromIdx, 1)
                                                            nextCascade.splice(cIdx, 0, fromId as any)

                                                            const nextBranches = s.branches.map((x) =>
                                                              x.id === b.id
                                                                ? {
                                                                    ...x,
                                                                    steps: x.steps.map((st) =>
                                                                      st.id === bs.id && st.type === 'kyc'
                                                                        ? { ...st, cascadeSources: nextCascade }
                                                                        : st
                                                                    ),
                                                                  }
                                                                : x
                                                            )
                                                            updateWorkflowStep(wf.id, s.id, { branches: nextBranches })
                                                            setDraggingCascadeId(null)
                                                          }}
                                                        >
                                                          <div className="flex items-center gap-2 min-w-0">
                                                            <GripVertical size={14} className="text-[var(--text-muted)]" />
                                                            <div className="text-sm text-[var(--text-primary)] truncate">{cascadeLabel(srcId)}</div>
                                                          </div>
                                                          <button
                                                            type="button"
                                                            className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                                                            onClick={() => {
                                                              const nextCascade = bs.cascadeSources.filter((x) => x !== srcId)
                                                              const nextBranches = s.branches.map((x) =>
                                                                x.id === b.id
                                                                  ? {
                                                                      ...x,
                                                                      steps: x.steps.map((st) =>
                                                                        st.id === bs.id && st.type === 'kyc'
                                                                          ? { ...st, cascadeSources: nextCascade }
                                                                          : st
                                                                      ),
                                                                    }
                                                                  : x
                                                              )
                                                              updateWorkflowStep(wf.id, s.id, { branches: nextBranches })
                                                            }}
                                                            disabled={disabled}
                                                          >
                                                            Remove
                                                          </button>
                                                        </div>
                                                      ))}
                                                    </div>

                                                    {!creditHeaderAllowed ? (
                                                      <div className="text-xs text-[var(--text-muted)]">
                                                        Credit Header sources require Reporting Entity to be set to Yes and credit header checks enabled in the KYC tab.
                                                      </div>
                                                    ) : null}
                                                  </div>
                                                </div>
                                              </div>
                                            ) : null}

                                            {bs.type === 'idv_biometrics' ? (
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                  <label className="block text-sm text-[var(--text-secondary)] mb-2">Requirement</label>
                                                  <select
                                                    className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                                    value={bs.requirement}
                                                    onChange={(e) => {
                                                      const nextReq: 'mandatory' | 'optional' = e.target.value === 'optional' ? 'optional' : 'mandatory'
                                                      const nextBranches = s.branches.map((x) =>
                                                        x.id === b.id
                                                          ? {
                                                              ...x,
                                                              steps: x.steps.map((st) =>
                                                                st.id === bs.id && st.type === 'idv_biometrics'
                                                                  ? { ...st, requirement: nextReq }
                                                                  : st
                                                              ),
                                                            }
                                                          : x
                                                      )
                                                      updateWorkflowStep(wf.id, s.id, { branches: nextBranches })
                                                    }}
                                                    disabled={disabled}
                                                  >
                                                    <option value="mandatory">Mandatory</option>
                                                    <option value="optional">Optional</option>
                                                  </select>
                                                </div>

                                                <div>
                                                  <label className="block text-sm text-[var(--text-secondary)] mb-2">Flow override (optional)</label>
                                                  <select
                                                    className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                                    value={bs.flowId || ''}
                                                    onChange={(e) => {
                                                      const nextBranches = s.branches.map((x) =>
                                                        x.id === b.id
                                                          ? {
                                                              ...x,
                                                              steps: x.steps.map((st) =>
                                                                st.id === bs.id && st.type === 'idv_biometrics'
                                                                  ? { ...st, flowId: e.target.value || null }
                                                                  : st
                                                              ),
                                                            }
                                                          : x
                                                      )
                                                      updateWorkflowStep(wf.id, s.id, { branches: nextBranches })
                                                    }}
                                                    disabled={disabled || !idvEnabled}
                                                  >
                                                    <option value="">Use workflow/default flow</option>
                                                    {idvFlows.map((f) => (
                                                      <option key={f.id} value={f.id}>
                                                        {f.name}
                                                      </option>
                                                    ))}
                                                  </select>
                                                  {!idvEnabled ? (
                                                    <div className="text-xs text-[var(--text-muted)] mt-1">
                                                      ID Verification is set to {idvDisabledLabel}. Enable it to select a flow.
                                                    </div>
                                                  ) : null}
                                                </div>

                                                <div>
                                                  <label className="block text-sm text-[var(--text-secondary)] mb-2">Flow details</label>
                                                  {(() => {
                                                    const d = formatIdvFlowSummary(bs, scope, wf.associatedFlowId)
                                                    if (!('flowName' in d)) {
                                                      return <div className="h-10 flex items-center text-sm text-[var(--text-secondary)]">{d.summary}</div>
                                                    }
                                                    return (
                                                      <div className="text-sm text-[var(--text-secondary)] space-y-1">
                                                        <div className="text-[var(--text-primary)] font-medium truncate">{d.flowName}</div>
                                                        <div className="text-xs text-[var(--text-muted)]">Steps: {d.flowSteps}</div>
                                                        <div className="text-xs text-[var(--text-muted)]">Vendor: {d.flowVendor || '—'}</div>
                                                        <div className="text-xs text-[var(--text-muted)]">Docs: {d.flowDocs}</div>
                                                      </div>
                                                    )
                                                  })()}
                                                </div>
                                              </div>
                                            ) : null}

                                            {bs.type === 'aml' ? (
                                              <div className="space-y-2">
                                                {!amlConfigured ? (
                                                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300">
                                                    AML is not configured yet. Set the AML level and vendor settings in the AML tab.
                                                  </div>
                                                ) : null}
                                                <div className="text-sm text-[var(--text-secondary)] space-y-1">
                                                  <div className="text-xs text-[var(--text-muted)]">Inherited from AML tab</div>
                                                  <div>Relationship: {amlRelationshipLabel}</div>
                                                  <div>Vendor: {amlVendorLabel}</div>
                                                  <div>Coverage: {amlCoverage}</div>
                                                </div>
                                              </div>
                                            ) : null}

                                            {bs.type === 'duplicate' ? (
                                              <div className="space-y-2">
                                                <label className="block text-xs text-[var(--text-secondary)]">
                                                  What will this duplicate check entail?
                                                </label>
                                                <textarea
                                                  className="w-full bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                                                  rows={2}
                                                  value={bs.notes}
                                                  onChange={(e) => {
                                                    const newBranches = b.steps.map((bss: WorkflowStep) =>
                                                      bss.id === bs.id ? { ...bss, notes: e.target.value } : bss
                                                    )
                                                    const updatedBranches = s.branches.map((br: RiskBranch) =>
                                                      br.id === b.id ? { ...br, steps: newBranches } : br
                                                    )
                                                    updateWorkflowStep(wf.id, s.id, { branches: updatedBranches })
                                                  }}
                                                  disabled={disabled}
                                                  placeholder="e.g. Match on full name + DOB across all entity types..."
                                                />
                                              </div>
                                            ) : null}
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                </div>

              </div>
              )}

              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">Notes</label>
                <input
                  type="text"
                  className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                  value={wf.notes}
                  onChange={(e) => updateWorkflow(wf.id, { notes: e.target.value })}
                  disabled={disabled}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
